/*
 * Scrollposition über eine Neuzeichnung halten (Hausregel „Scroll bei Neuzeichnen“).
 *
 * In der Anwendung rollt nicht das Fenster, sondern ein innerer Container (die Schale ist 100dvh hoch, die Spalte und das Panel
 * der Akte rollen in sich). Wer Inhalt über einem Bedienelement entfernt oder einschiebt, gleicht deshalb dort aus, wo wirklich
 * gerollt wird, und nur ohne rollenden Vorfahren am Fenster.
 */

/** Der Vorfahr in der gezeichneten Baumstruktur: bei einem eingeschobenen Knoten der Slot, an der Schattengrenze der Wirt. */
function composedParent(node: Element): Element | null {
  const root = node.getRootNode();
  return node.assignedSlot ?? node.parentElement ?? (root instanceof ShadowRoot ? root.host : null);
}

/** Rollt der Knoten in sich: Überlauf `auto`/`scroll` UND mehr Inhalt, als hineinpasst. */
function scrolls(node: Element): boolean {
  return ["auto", "scroll"].includes(getComputedStyle(node).overflowY) && node.scrollHeight > node.clientHeight;
}

/**
 * Der nächste Vorfahr von `element`, der rollt (über Schattengrenzen hinweg), sonst `null`: Dann rollt das Fenster. `body` und
 * `html` zählen nie: Ihr Überlauf ist der des Fensters, auch wenn ein Stilblatt `body { overflow: auto }` setzt.
 */
export function scrollingAncestor(element: Element): Element | null {
  for (let node = composedParent(element); node !== null && node !== document.body && node !== document.documentElement;
    node = composedParent(node)) {
    if (scrolls(node)) return node;
  }
  return null;
}

/** Die oberste gezeigte Kachel einer Wand im Bild des rollenden Rahmens, nie die bewegte `skipId`: Sie wechselt ihren Platz oder verschwindet. */
export function topTileInView(wall: Element, skipId?: string): Element | undefined {
  const top = scrollingAncestor(wall)?.getBoundingClientRect().top ?? 0;
  return [...wall.children].find((tile) => !tile.hasAttribute("ausgeblendet") && tile.id !== skipId && tile.getBoundingClientRect().bottom > top);
}

/**
 * Merkt die Lage von `element` im Fenster. Der Rückgabewert rollt nach der Zeichnung so weit, dass es wieder dort steht — im
 * rollenden Vorfahren, der VOR der Änderung gesucht wird (danach passt der Inhalt vielleicht ganz hinein und rollt nicht mehr),
 * sonst im Fenster. Hat sich nichts verschoben, rollt nichts. Ist der Anker bis dahin aus dem Baum genommen oder nicht mehr
 * gezeigt (`display: none`, etwa die eben ausgeblendete Kachel), hat er keine Lage mehr: Dann rollt nichts.
 */
export function keepInView(element: Element): () => void {
  const before = element.getBoundingClientRect().top;
  const scroller = scrollingAncestor(element);
  return () => {
    if (!element.isConnected || element.checkVisibility?.() === false) return;
    // Auf ganze Pixel: WebKit rundet Bruchteile beim Rollen ab, sonst wanderte der Anker bei jedem Ausgleich um bis zu
    // einen halben Pixel weiter (Board-Fokus an und aus, Aufgabe 19). Ohne Animation, auch wenn ein Stilblatt
    // `scroll-behavior: smooth` setzt: Der Ausgleich soll nicht sichtbar gleiten.
    const shift = Math.round(element.getBoundingClientRect().top - before);
    if (shift !== 0) (scroller ?? window).scrollBy({ top: shift, behavior: "instant" });
  };
}
